/**
 * The granny flat plan in one sentence — the SAME sentence on the plan summary rail (Manual
 * Control) and on every Suggestion Engine card, so the two surfaces cannot describe a plan
 * differently. Measured against NOT ENTERING THE ARRANGEMENT — the only honest benchmark for an
 * elective move.
 *
 * THE GIFT IS THE THING THAT SURPRISES PEOPLE. Pay more than Centrelink's reasonable value and the
 * excess is deprivation — counted as an asset, and deemed to earn income, for five years even
 * though the money is gone. A summary that reports the pension movement without naming this leaves
 * the client with the effect and no cause. Toned 'bad' because it IS a cost, not a neutral fact.
 * A lifetime annuity with nothing returned on death is named the same way (owner, 24 Sep 2026).
 */
import React from 'react';
import { formatCurrency } from '../../utils/index.ts';
import {
    pensionClause,
    cashflowClause,
    estateClause,
    plainClause,
    RecapClauses,
    type RecapClause,
} from '../../components/strategy/recapClauses.tsx';

export interface GfInBriefAnnuity {
    amount: number;
    /** 0 = nothing returned on death; 100 = the capital comes back. */
    residualPercent: number;
    ownerName: string;
}

export interface GfInBriefInput {
    /** Cash paid plus any property transferred for the right. */
    entryContribution: number;
    propertyTransferred: number;
    justMovingIn: boolean;
    /** What was paid above Centrelink's reasonable value — counted as a gift for five years. */
    deprivation: number;
    /** The former home: kept (vacant or rented out), sold, or transferred — null when there is no home. */
    home: 'kept' | 'rented' | 'sold' | 'transferred' | null;
    keptHomeRentPerWeek?: number;
    /** On a sale: what was left after the right was paid for. */
    saleResidual?: number;
    pensionBefore: number;
    pensionAfter: number;
    cashBefore: number;
    cashAfter: number;
    planLegacy: number | null;
    baseLegacy: number | null;
    legacyYears: number;
    annuity?: GfInBriefAnnuity | null;
}

/** The plain-words fact of a nil-residual annuity, used by the sentence, the card and the recap. */
export function nilResidualAnnuityText(a: GfInBriefAnnuity): string {
    return `the ${formatCurrency(a.amount)} lifetime annuity returns nothing when ${a.ownerName} ${a.ownerName === 'you' ? 'die' : 'dies'} — that money leaves the estate the day it is bought`;
}

export function buildGfInBriefClauses(i: GfInBriefInput): RecapClause[] {
    const clauses: RecapClause[] = [];
    if (i.justMovingIn || (i.entryContribution <= 0 && i.home !== 'transferred')) {
        clauses.push(plainClause('you move in without paying anything for the right to live there'));
    } else if (i.home === 'transferred' && i.propertyTransferred > 0) {
        clauses.push(plainClause(`your ${formatCurrency(i.propertyTransferred)} home is signed over for the right to live there${i.entryContribution > i.propertyTransferred ? `, with ${formatCurrency(i.entryContribution - i.propertyTransferred)} on top` : ''}`));
    } else {
        clauses.push(plainClause(`you give ${formatCurrency(i.entryContribution)} for the right to live there`));
    }
    if (i.deprivation > 0) {
        clauses.push({ tone: 'bad', text: `${formatCurrency(i.deprivation)} of that counts as a gift, so Centrelink keeps counting it for five years` });
    } else if (i.entryContribution > 0) {
        clauses.push({ tone: 'good', text: 'none of it counts as a gift' });
    }
    if (i.home === 'kept') clauses.push(plainClause('the former home is kept empty, and is counted by Centrelink from the day you move'));
    else if (i.home === 'rented') clauses.push(plainClause(`the former home is kept and rented out${(i.keptHomeRentPerWeek ?? 0) > 0 ? ` for ${formatCurrency(Math.round((i.keptHomeRentPerWeek ?? 0) * 52))} a year` : ''}, and is counted by Centrelink`));
    else if (i.home === 'sold') clauses.push(plainClause(`the home is sold${(i.saleResidual ?? 0) > 0 ? `, leaving ${formatCurrency(i.saleResidual ?? 0)} after the right is paid for` : ''}`));
    if (i.annuity && i.annuity.amount > 0 && Math.round(i.annuity.residualPercent) === 0) {
        clauses.push({ tone: 'bad', text: nilResidualAnnuityText(i.annuity) });
    }
    clauses.push(pensionClause(Math.round(i.pensionAfter - i.pensionBefore)));
    const cashflowDelta = Math.round(i.cashAfter - i.cashBefore);
    if (cashflowDelta !== 0) clauses.push(cashflowClause(cashflowDelta, i.cashAfter));
    if (i.planLegacy != null && i.baseLegacy != null) {
        const legacyDelta = i.planLegacy - i.baseLegacy;
        const e = estateClause(legacyDelta, Number(i.legacyYears) || 0);
        clauses.push(Math.round(legacyDelta) === 0 ? e : { ...e, text: e.text.replace(' ahead after', ' ahead of not entering after').replace(' behind after', ' behind not entering after') });
    }
    return clauses;
}

export function GfInBrief(props: GfInBriefInput) {
    const clauses = buildGfInBriefClauses(props);
    if (!clauses.length) return null;
    return <RecapClauses clauses={clauses} />;
}
